import React, { Suspense, useCallback, useMemo } from 'react';
import { useRedirectToStripeUrlOrStartTrial } from 'shared/hooks/use-redirect-to-stripe-url-or-start-trial';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { QueryParamOptions, useQueryParams } from 'shared/lib/react-router-dom';
import { ActionEvents, OnboardingPlanOptions } from 'shared/services/tracking';
import { Price, PriceInterval } from 'shared/types/price';

import { OnboardingContext } from './onboarding-context';
import { Premium, PremiumLoading } from './premium';

export type OnboardingProviderProps = {
  children: React.ReactNode;
};

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const track = useTrackEvents();
  const [
    {
      [QueryParamOptions.OnboardingPremium]: onboardingPremium,
      [QueryParamOptions.Premium]: premium,
    },
    setQueryParams,
  ] = useQueryParams([
    QueryParamOptions.OnboardingPremium,
    QueryParamOptions.Purchase,
    QueryParamOptions.Premium,
  ]);

  const closeOnboarding = useCallback(() => {
    // clear the onboarding-premium and premium query param so the user is not shown the purchase dialog again
    setQueryParams(
      {
        [QueryParamOptions.Premium]: premium,
      },
      true,
    );

    track(ActionEvents.OnboardingPremiumSkipSubmit, undefined);
  }, [premium, setQueryParams, track]);

  const { redirectOrStartTrial, isLoading } =
    useRedirectToStripeUrlOrStartTrial();
  const purchasePremium = async (price?: Price) => {
    if (!price) {
      return;
    }

    track(ActionEvents.OnboardingPremiumTrialSubmit, {
      selected_plan:
        price.interval === PriceInterval.Month
          ? OnboardingPlanOptions.Monthly
          : price.interval === PriceInterval.Year
            ? OnboardingPlanOptions.Yearly
            : OnboardingPlanOptions.Lifetime,
    });

    const trialStarted = await redirectOrStartTrial({
      interval: price.interval,
      coupon: price.coupon?.id,
    });

    // explicit check on boolean
    if (trialStarted === true) {
      queueMicrotask(() =>
        setQueryParams(
          {
            [QueryParamOptions.Premium]: 'true',
          },
          true,
        ),
      );
      closeOnboarding();
    }
  };

  const value = useMemo(() => ({}), []);

  if (onboardingPremium) {
    return (
      <OnboardingContext.Provider value={value}>
        <Suspense fallback={<PremiumLoading onSkip={closeOnboarding} />}>
          <Premium
            onSkip={closeOnboarding}
            onSubmit={purchasePremium}
            isLoading={isLoading}
          />
        </Suspense>
      </OnboardingContext.Provider>
    );
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
